import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, timeAgo } from "@/api";
import { toast } from "sonner";
import { Sparkle, ArrowsClockwise, Trash, CheckCircle, XCircle } from "@phosphor-icons/react";

// Compact bar showing the win-rate lift a rule offers vs. its baseline.
function EdgeBadge({ edge }) {
  const color = edge >= 20 ? "#00FF66" : edge >= 15 ? "#8DD35B" : "#F5C518";
  return (
    <span
      className="font-mono text-[10px] px-1.5 py-0.5 border"
      style={{ borderColor: `${color}55`, color, background: `${color}0f` }}
    >
      +{edge.toFixed(1)}pp
    </span>
  );
}

export function AutoDiscoveredIndicators() {
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["learned-indicators"],
    queryFn: () => api.learnedIndicators(true),
    refetchInterval: 60_000,
  });

  const discover = useMutation({
    mutationFn: api.discoverLearnedIndicators,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["learned-indicators"] });
      if (data.skipped_reason === "not_enough_samples") {
        toast.warning(
          `Only ${data.resolved_samples} resolved trade(s) — need 20+ to mine reliable patterns.`,
        );
      } else {
        toast.success(
          `Mined ${data.resolved_samples} trades → ${data.active_rules} active rules`,
        );
      }
    },
    onError: () => toast.error("Discovery pass failed"),
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }) => api.toggleLearnedIndicator(id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["learned-indicators"] }),
  });

  const reset = useMutation({
    mutationFn: api.resetLearnedIndicators,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["learned-indicators"] });
      toast.info("All learned indicators cleared");
    },
  });

  const rules = q.data?.rules || [];
  const active = q.data?.active_count || 0;
  const maxActive = q.data?.max_active || 40;
  const lastRun = q.data?.last_run;

  return (
    <div
      className="border border-white/10 bg-[#0d0d0d]"
      data-testid="auto-discovered-indicators"
    >
      <div className="px-3 py-2 border-b border-white/10 flex items-center gap-3 flex-wrap">
        <Sparkle size={14} className="text-[#F5C518]" weight="fill" />
        <span className="font-display text-sm tracking-widest uppercase text-white">
          Self-Evolving Indicators
        </span>
        <span className="font-mono text-[10px] text-white/40">
          Bot mines its own trade history for winning conjunctions · auto-injects them as bonus score
        </span>
        <div className="ml-auto flex items-center gap-2">
          <span
            className="font-mono text-[10px] text-white/50"
            data-testid="learned-active-count"
          >
            {active}/{maxActive} active
          </span>
          <button
            data-testid="discover-btn"
            onClick={() => discover.mutate()}
            disabled={discover.isPending}
            className="px-3 py-1 border border-[#F5C518]/50 hover:bg-[#F5C518]/10 text-[#F5C518] font-mono text-[10px] uppercase tracking-widest disabled:opacity-40 flex items-center gap-1"
          >
            <ArrowsClockwise size={10} weight="bold" />
            {discover.isPending ? "Mining…" : "Re-Mine Now"}
          </button>
          <button
            data-testid="reset-learned-btn"
            onClick={() => {
              if (confirm("Wipe all auto-discovered rules? Next pass will rebuild.")) reset.mutate();
            }}
            className="px-2 py-1 border border-white/10 hover:bg-white/5 text-white/50 font-mono text-[10px] uppercase tracking-widest flex items-center gap-1"
          >
            <Trash size={10} />
          </button>
        </div>
      </div>

      {lastRun && (
        <div className="px-3 py-1.5 border-b border-white/5 font-mono text-[10px] text-white/40 flex flex-wrap items-center gap-4">
          <span>
            Last mining pass: <span className="text-white/70">{timeAgo(lastRun.at)}</span>
          </span>
          <span>
            Samples analyzed: <span className="text-white/70">{lastRun.resolved_samples}</span>
          </span>
          {lastRun.skipped_reason && (
            <span className="text-[#F5C518]">
              skipped: {lastRun.skipped_reason.replace(/_/g, " ")}
            </span>
          )}
          {!lastRun.skipped_reason && (
            <>
              <span>
                Upserted: <span className="text-white/70">{lastRun.discovered_or_updated}</span>
              </span>
              <span>
                Disabled: <span className="text-white/70">{lastRun.disabled}</span>
              </span>
            </>
          )}
        </div>
      )}

      {rules.length === 0 && (
        <div
          className="px-3 py-8 text-center text-white/40 font-mono text-[11px]"
          data-testid="learned-empty"
        >
          No rules discovered yet. The engine needs at least{" "}
          <span className="text-[#F5C518]">20 resolved trades</span> with clear win/loss outcomes before it can
          find reliable patterns.
          <br />
          As trades accumulate, the bot will discover winning combinations like{" "}
          <span className="text-white/60 italic">&quot;ema_stack ∧ adx&gt;=30&quot;</span> and auto-inject them as bonus
          score in every scan.
        </div>
      )}

      {rules.length > 0 && (
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full font-mono text-[11px]" data-testid="learned-rules-table">
            <thead className="sticky top-0 bg-[#0d0d0d] border-b border-white/10">
              <tr className="text-left text-white/40 uppercase tracking-wider">
                <th className="px-3 py-1.5 font-normal">Rule</th>
                <th className="px-2 py-1.5 font-normal text-right">Win %</th>
                <th className="px-2 py-1.5 font-normal text-right">Edge</th>
                <th className="px-2 py-1.5 font-normal text-right">N</th>
                <th className="px-2 py-1.5 font-normal text-right">+Score</th>
                <th className="px-2 py-1.5 font-normal text-center">On</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => {
                const wr = r.win_rate || 0;
                const wrColor =
                  wr >= 70 ? "#00FF66" : wr >= 60 ? "#8DD35B" : wr >= 50 ? "#F5C518" : "#FF3B30";
                return (
                  <tr
                    key={r.id}
                    className={`border-b border-white/5 hover:bg-white/[0.02] ${
                      r.enabled ? "" : "opacity-40"
                    }`}
                    data-testid={`learned-rule-${r.id?.slice(0, 8)}`}
                  >
                    <td className="px-3 py-1.5 text-white/80">
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-1 h-4 ${
                            r.direction === "long" ? "bg-[#00FF66]" : "bg-[#FF3B30]"
                          }`}
                        />
                        <span className="text-white/40 uppercase text-[9px]">
                          {r.mode === "any" ? "all modes" : r.mode}
                        </span>
                        <span>{r.description}</span>
                        {r.decayed && (
                          <span className="text-[#FF3B30] text-[9px] uppercase">decayed</span>
                        )}
                        {r.capped_out && (
                          <span className="text-white/30 text-[9px] uppercase">capped</span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right" style={{ color: wrColor }}>
                      {wr.toFixed(1)}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <EdgeBadge edge={r.edge || 0} />
                    </td>
                    <td className="px-2 py-1.5 text-right text-white/60">{r.sample_count}</td>
                    <td className="px-2 py-1.5 text-right text-[#F5C518]">+{r.bonus_weight}</td>
                    <td className="px-2 py-1.5 text-center">
                      <button
                        data-testid={`toggle-${r.id?.slice(0, 8)}`}
                        onClick={() => toggle.mutate({ id: r.id, enabled: !r.enabled })}
                        className="inline-flex items-center"
                        title={r.enabled ? "Disable rule" : "Enable rule"}
                      >
                        {r.enabled ? (
                          <CheckCircle size={14} weight="fill" className="text-[#00FF66]" />
                        ) : (
                          <XCircle size={14} weight="regular" className="text-white/30" />
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
